import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, LinearProgress, Tooltip, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SftGlobalData from '../library/SftGlobalData';

/**
 * "Start from a Word document" for any Unlayer editor.
 *
 * Self-contained on purpose: it only needs a way to reach the editor instance
 * and (optionally) a way to store an image. It knows nothing about webshops,
 * languages or how the host saves, so every Unlayer location can drop it next
 * to its editor:
 *
 *   <UnlayerDocumentImport
 *       getEditor={() => editorRef.current?.editor}
 *       apiKey={api}
 *       uploadImage={uploadShopImage}     // (dataUrl, filename) => Promise<url>
 *       displayMode="web"
 *       t={t}
 *   />
 *
 * Flow: pick .docx -> (confirm when the editor already has content) -> auth
 * converts it to a design -> images are stored through `uploadImage` and their
 * tokens swapped for real URLs -> editor.loadDesign(). loadDesign fires
 * `design:updated`, so hosts that already sync on that event pick the import up
 * without extra wiring; the others can use `onImported`.
 *
 * Editors without an image store of their own (the e-mail editors: a data URI
 * is blocked by Gmail/Outlook) pass imageFallback="placeholder": every image
 * becomes an EMPTY image block at the right spot, which the user fills through
 * Unlayer's own upload.
 *
 * The conversion itself lives in auth (route documentimport&action=convert).
 */

const MAX_FILE_BYTES = 15 * 1024 * 1024;
// An image that could not be stored still has to show up: small ones are inlined.
const MAX_INLINE_IMAGE_CHARS = 700 * 1024;

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsDataURL(file);
});

const countContents = (design) => {
    try {
        return (design?.body?.rows || []).reduce(
            (n, row) => n + (row.columns || []).reduce((m, col) => m + (col.contents || []).length, 0), 0);
    } catch { return 0; }
};

const UnlayerDocumentImport = ({
    getEditor, apiKey, uploadImage, displayMode = 'web', t, disabled = false,
    onImported, size = 'small', variant = 'outlined', label, sx, imageFallback = 'inline',
}) => {
    const tr = (key, fallback) => {
        if (typeof t !== 'function') return fallback;
        const value = t(key);
        return (!value || value === key) ? fallback : value;
    };

    const inputRef = useRef(null);
    const aliveRef = useRef(true);
    useEffect(() => () => { aliveRef.current = false; }, []);

    const [pendingFile, setPendingFile] = useState(null);   // waiting for the overwrite confirmation
    const [busy, setBusy] = useState(null);                 // { step, done, total }
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);             // { warnings, stats, filename }

    const warningText = (w) => {
        const details = (w.details || []).join(', ');
        switch (w.code) {
            case 'fonts_not_websafe':  return tr('cfg_docimport_warn_fonts', 'Deze lettertypes bestaan niet op elk toestel; er is een gelijkend lettertype als terugval ingesteld') + `: ${details}`;
            case 'table_as_html':      return tr('cfg_docimport_warn_table', 'Tabellen zijn als HTML-blok overgenomen: ze zien eruit zoals in het document, maar zijn enkel als code aanpasbaar.');
            case 'floating_image':     return tr('cfg_docimport_warn_floating', 'Zwevende afbeeldingen (met tekstomloop) zijn als gewoon blok in de tekst geplaatst.');
            case 'inline_image_split': return tr('cfg_docimport_warn_inlineimg', 'Een afbeelding midden in een alinea is als apart blok geplaatst.');
            case 'textbox':            return tr('cfg_docimport_warn_textbox', 'Tekstvakken zijn in de gewone tekststroom gezet; hun exacte positie kon niet behouden blijven.');
            case 'header_footer':      return tr('cfg_docimport_warn_headerfooter', 'Kop- en voetteksten van het document zijn niet overgenomen.');
            case 'footnotes':          return tr('cfg_docimport_warn_footnotes', 'Voetnoten zijn niet overgenomen.');
            case 'tabs':               return tr('cfg_docimport_warn_tabs', 'Tabs zijn vervangen door een vaste spatie; uitlijning met tabs kan afwijken.');
            case 'page_breaks':        return tr('cfg_docimport_warn_pagebreaks', 'Pagina-einden zijn genegeerd.');
            case 'multi_column':       return tr('cfg_docimport_warn_multicol', 'Tekst in krantenkolommen is als één kolom overgenomen.');
            case 'toc_skipped':        return tr('cfg_docimport_warn_toc', 'De inhoudsopgave is overgeslagen.');
            case 'symbols':            return tr('cfg_docimport_warn_symbols', 'Speciale symbolen (Wingdings e.d.) zijn niet overgenomen.');
            case 'tracked_changes':    return tr('cfg_docimport_warn_tracked', 'Het document bevat bijgehouden wijzigingen: invoegingen zijn overgenomen, verwijderingen niet.');
            case 'unsupported_image':  return tr('cfg_docimport_warn_imgformat', 'Afbeeldingen in een formaat dat browsers niet tonen zijn overgeslagen') + `: ${details}`;
            case 'image_skipped':      return tr('cfg_docimport_warn_imgskipped', 'Te grote of te veel afbeeldingen zijn overgeslagen') + `: ${details}`;
            case 'images_placeholder': return tr('cfg_docimport_warn_imgplaceholder', 'Afbeeldingen zijn niet automatisch overgenomen: op hun plaats staat een leeg afbeeldingsblok. Klik erop om de afbeelding toe te voegen.') + (details ? ` (${details})` : '');
            case 'image_upload_failed': return tr('cfg_docimport_warn_imgupload', 'Deze afbeeldingen konden niet op de server bewaard worden') + `: ${details}`;
            default:                   return w.code + (details ? `: ${details}` : '');
        }
    };

    const errorText = (res) => {
        switch (res?.translation_key) {
            case 'docimport_pdf_not_supported': return tr('cfg_docimport_err_pdf', 'PDF wordt nog niet ondersteund. Open de PDF in Word en bewaar hem als .docx.');
            case 'docimport_legacy_doc':        return tr('cfg_docimport_err_doc', 'Dit is het oude .doc-formaat. Bewaar het document in Word als .docx.');
            case 'docimport_too_large':         return tr('cfg_docimport_err_large', 'Het document is te groot (maximaal 15 MB).');
            case 'docimport_invalid_file':      return tr('cfg_docimport_err_invalid', 'Dit is geen geldig Word-document (.docx).');
            case 'docimport_empty':             return tr('cfg_docimport_err_empty', 'Het document bevat geen inhoud.');
            default:                            return res?.message || tr('cfg_docimport_err_failed', 'Het document kon niet omgezet worden.');
        }
    };

    const runImport = async (file) => {
        const editor = typeof getEditor === 'function' ? getEditor() : null;
        if (!editor) {
            setError(tr('cfg_docimport_err_editor', 'De editor is nog niet klaar. Probeer het zo meteen opnieuw.'));
            return;
        }
        setError(null);
        setBusy({ step: 'convert', done: 0, total: 0 });
        try {
            const dataUrl = await readAsDataUrl(file);
            const res = await SftGlobalData.post('documentimport&action=convert', {
                key:          apiKey,
                filename:     file.name,
                file_base64:  dataUrl,
                display_mode: displayMode === 'email' ? 'email' : 'web',
            });
            if (!aliveRef.current) return;
            if (!res?.success || !res.data?.design) {
                setError(errorText(res));
                return;
            }

            const { design, images = [], image_token: token = 'sft-import://', stats } = res.data;
            const warnings = [...(res.data.warnings || [])];
            let raw = JSON.stringify(design);

            const failed = [];
            const placeholders = [];
            const usePlaceholders = imageFallback === 'placeholder' && typeof uploadImage !== 'function';
            const uploadCount = usePlaceholders ? 0 : images.length;
            for (let i = 0; i < uploadCount; i += 1) {
                if (!aliveRef.current) return;
                setBusy({ step: 'images', done: i, total: images.length });
                const img = images[i];
                const imgDataUrl = `data:${img.mime};base64,${img.base64}`;
                let url = null;
                if (typeof uploadImage === 'function') {
                    try { url = await uploadImage(imgDataUrl, img.filename); } catch { url = null; }
                }
                if (!url) {
                    failed.push(img.filename);
                    url = imgDataUrl.length <= MAX_INLINE_IMAGE_CHARS ? imgDataUrl : '';
                }
                // JSON-escape the replacement: it lands inside string values of the serialised design
                raw = raw.split(token + img.id).join(JSON.stringify(url).slice(1, -1));
            }
            if (failed.length) warnings.push({ code: 'image_upload_failed', details: failed });
            if (!aliveRef.current) return;

            if (usePlaceholders && images.length) {
                // images inside HTML tables cannot become a block: drop the tag
                raw = raw.replace(new RegExp('<img[^>]*?' + token.replace(/[/:]/g, '\\$&') + '[^>]*?>', 'g'), '');
            }
            const finalDesign = JSON.parse(raw);
            if (usePlaceholders && images.length) {
                (finalDesign.body?.rows || []).forEach((row) => (row.columns || []).forEach((col) => (col.contents || []).forEach((content) => {
                    const url = content?.values?.src?.url;
                    if (content.type === 'image' && typeof url === 'string' && url.indexOf(token) === 0) {
                        const img = images.find((x) => token + x.id === url);
                        if (img) placeholders.push(img.filename.replace(/^import-/, ''));
                        delete content.values.src; // no src = Unlayer's own empty image block
                    }
                })));
                warnings.push({ code: 'images_placeholder', details: placeholders });
            }
            editor.loadDesign(finalDesign);
            if (typeof onImported === 'function') {
                try { onImported({ design: finalDesign, warnings, stats }); } catch { /* host errors must not break the import */ }
            }
            setResult({ warnings, stats, filename: file.name });
        } catch (e) {
            if (aliveRef.current) setError(e?.message || tr('cfg_docimport_err_failed', 'Het document kon niet omgezet worden.'));
        } finally {
            if (aliveRef.current) setBusy(null);
        }
    };

    const onFilePicked = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setResult(null);
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (ext === 'pdf') { setError(errorText({ translation_key: 'docimport_pdf_not_supported' })); return; }
        if (ext === 'doc') { setError(errorText({ translation_key: 'docimport_legacy_doc' })); return; }
        if (ext !== 'docx') { setError(errorText({ translation_key: 'docimport_invalid_file' })); return; }
        if (file.size > MAX_FILE_BYTES) { setError(errorText({ translation_key: 'docimport_too_large' })); return; }

        const editor = typeof getEditor === 'function' ? getEditor() : null;
        if (!editor) { runImport(file); return; }
        // Only ask before overwriting when there is something to overwrite.
        let answered = false;
        const proceed = (hasContent) => {
            if (answered) return;
            answered = true;
            if (hasContent) setPendingFile(file); else runImport(file);
        };
        try {
            editor.saveDesign((design) => proceed(countContents(design) > 0));
            setTimeout(() => proceed(true), 1500); // editor did not answer: be careful, ask
        } catch { proceed(true); }
    };

    const isBusy = busy !== null;
    const busyLabel = !busy ? '' : (busy.step === 'images'
        ? `${tr('cfg_docimport_busy_images', 'Afbeeldingen bewaren')} ${busy.done + 1}/${busy.total}`
        : tr('cfg_docimport_busy_convert', 'Document omzetten…'));

    return (
        <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 1, ...(sx || {}) }}>
            {/* inline display:none -- a global stylesheet overrides the bare `hidden` attribute on inputs */}
            <input ref={inputRef} type="file" accept=".docx,.doc,.pdf" onChange={onFilePicked} style={{ display: 'none' }} tabIndex={-1} aria-hidden="true" />
            <Tooltip title={tr('cfg_docimport_tooltip', 'Start vanaf een Word-document (.docx): de opmaak wordt omgezet naar bewerkbare blokken.')}>
                <span>
                    <Button
                        size={size}
                        variant={variant}
                        disabled={disabled || isBusy}
                        startIcon={isBusy ? <CircularProgress size={14} color="inherit" /> : <UploadFileIcon fontSize="small" />}
                        onClick={() => inputRef.current?.click()}
                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                        {isBusy ? busyLabel : (label || tr('cfg_docimport_button', 'Start vanaf document'))}
                    </Button>
                </span>
            </Tooltip>

            {/* overwrite confirmation */}
            <Dialog open={!!pendingFile} onClose={() => setPendingFile(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{tr('cfg_docimport_confirm_title', 'Huidige inhoud vervangen?')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {tr('cfg_docimport_confirm_body', 'De editor bevat al inhoud. Die wordt volledig vervangen door het document')}
                        {pendingFile ? ` "${pendingFile.name}".` : '.'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingFile(null)}>{tr('cfg_docimport_cancel', 'Annuleren')}</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => { const f = pendingFile; setPendingFile(null); runImport(f); }}
                    >
                        {tr('cfg_docimport_confirm_ok', 'Vervangen')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* progress while images are stored */}
            <Dialog open={isBusy} maxWidth="xs" fullWidth>
                <DialogContent sx={{ py: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>{busyLabel}</Typography>
                    <LinearProgress
                        variant={busy?.step === 'images' && busy.total > 0 ? 'determinate' : 'indeterminate'}
                        value={busy?.step === 'images' && busy.total > 0 ? (busy.done / busy.total) * 100 : undefined}
                    />
                </DialogContent>
            </Dialog>

            {/* error */}
            <Dialog open={!!error} onClose={() => setError(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{tr('cfg_docimport_err_title', 'Importeren mislukt')}</DialogTitle>
                <DialogContent><Alert severity="error">{error}</Alert></DialogContent>
                <DialogActions><Button onClick={() => setError(null)}>{tr('cfg_docimport_close', 'Sluiten')}</Button></DialogActions>
            </Dialog>

            {/* result + what could not be taken over exactly */}
            <Dialog open={!!result} onClose={() => setResult(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{tr('cfg_docimport_done_title', 'Document geïmporteerd')}</DialogTitle>
                <DialogContent>
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {tr('cfg_docimport_done_body', 'Het document staat in de editor. Kijk het na en bewaar zoals gewoonlijk.')}
                        {result?.stats ? ` (${result.stats.headings} ${tr('cfg_docimport_stat_headings', 'koppen')}, ${result.stats.paragraphs} ${tr('cfg_docimport_stat_paragraphs', 'alinea\'s')}, ${result.stats.images} ${tr('cfg_docimport_stat_images', 'afbeeldingen')}, ${result.stats.tables} ${tr('cfg_docimport_stat_tables', 'tabellen')})` : ''}
                    </Alert>
                    {result?.warnings?.length > 0 && (
                        <>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                {tr('cfg_docimport_warn_title', 'Niet exact overgenomen — kijk dit na:')}
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75, fontSize: 14 } }}>
                                {result.warnings.map((w) => <li key={w.code}>{warningText(w)}</li>)}
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions><Button variant="contained" onClick={() => setResult(null)}>{tr('cfg_docimport_close', 'Sluiten')}</Button></DialogActions>
            </Dialog>
        </Box>
    );
};

UnlayerDocumentImport.propTypes = {
    getEditor:   PropTypes.func.isRequired,
    apiKey:      PropTypes.string,
    uploadImage: PropTypes.func,
    displayMode: PropTypes.oneOf(['web', 'email']),
    t:           PropTypes.func,
    disabled:    PropTypes.bool,
    onImported:  PropTypes.func,
    size:        PropTypes.string,
    variant:     PropTypes.string,
    label:       PropTypes.string,
    sx:          PropTypes.object,
    imageFallback: PropTypes.oneOf(['inline', 'placeholder']),
};

export default UnlayerDocumentImport;
